/**
 * PathBreadcrumb/useCrumbLabel/index.ts
 * desc: the text of a crumb — a fixed label translated, or an entity named the way its own page names it. A kind
 *       that has a name shows nothing until the name is there; a kind without one (trips, rule set evaluations,
 *       whose pages title themselves by id) shows its id at once
 * note: no fetching of its own: RTK Query kinds read the cache the page's own query fills — through the endpoint's
 *       selector, never a query hook, which would subscribe and fetch from the header before the page mounts — and
 *       legacy kinds read what the page's thunk stored, only while the stored id is the crumb's. Accepts an absent
 *       crumb (a page without a parent) so callers keep a fixed hook order
 */

import type { BreadcrumbEntity } from '../../../router/types';
import type { Crumb } from '../../../router/utilities';
import type { RootState } from '../../../store';
import { environmentsApi } from '../../../store/api/environments';
import { orderSchemasApi } from '../../../store/api/order-schemas';
import { ruleSetsApi } from '../../../store/api/rule-sets';
import { rulesApi } from '../../../store/api/rules';
import { stepFactoriesApi } from '../../../store/api/step-factories';
import { useAppSelector } from '../../../store/hooks';
import { translate } from '../../../utilities/localization';

interface NamedEntity {
    id?: string;
    name?: string;
}

interface Order {
    id?: string;
    purchaseOrderNumber?: string;
}

// a version is identified by versionId, not id
interface StepFactoryVersion {
    versionId?: string;
    version?: number;
}

interface User {
    id?: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
}

// the still-JS api slices type their endpoints loosely — pin the contract this label relies on: the endpoint's
// cache selector, which reads whatever the page's own query stored without subscribing
type CachedQuery = (id: string) => (state: RootState) => { data?: NamedEntity } | undefined;
const selectEnvironment = environmentsApi.endpoints.getEnvironment.select as unknown as CachedQuery;
const selectOrderSchema = orderSchemasApi.endpoints.getOrderSchema.select as unknown as CachedQuery;
const selectRuleSet = ruleSetsApi.endpoints.getRuleSetById.select as unknown as CachedQuery;
const selectRule = rulesApi.endpoints.getRuleById.select as unknown as CachedQuery;
const selectStepFactory = stepFactoriesApi.endpoints.getStepFactory.select as unknown as CachedQuery;

// the slices of the still-untyped legacy store this label reads, cast at the boundary
interface DetailSlice { detail?: NamedEntity }
interface ModelsSlice { detail?: { model?: NamedEntity }; list?: { models?: NamedEntity[] } }
interface PathsSlice { detail?: { path?: NamedEntity } }
interface ReviewQueuesSlice { queueDetail?: NamedEntity; queueList?: NamedEntity[] }
interface SourcesSlice { sourceDetail?: NamedEntity; sourceList?: NamedEntity[] }
interface StepFactoriesSlice { stepFactoryDetail?: NamedEntity; stepFactoryVersionDetail?: StepFactoryVersion }
interface UsersSlice { detail?: User }

// a page may still hold the previous entity of its kind — only a matching id counts
const _whenMatching = <T extends { id?: string }>(entity: T | undefined, id: string, label: (found: T) => string | undefined) =>
    (entity?.id === id ? label(entity) || undefined : undefined);

const _nameOf = (entity: NamedEntity | undefined, id: string) => _whenMatching(entity, id, (found) => found.name);

const _poNumber = (order?: Order) => order?.purchaseOrderNumber;

const _userLabel = (user: User) => user.name || [ user.firstName, user.lastName ].filter(Boolean).join(' ') || user.email;

const _versionLabel = (version: StepFactoryVersion) =>
    (typeof version.version === 'number' ? translate('breadcrumb.version', { number: version.version }) : undefined);

type StoreReader = (state: RootState, id: string) => string | undefined;

const _cachedName = (select: CachedQuery, state: RootState, id: string) => select(id)(state)?.data?.name;
// a version is cached under its factory's id as well, so the cache is searched for the one whose version id matches
const _cachedVersion = (state: RootState, id: string) => {
    const ref = stepFactoriesApi.util.selectCachedArgsForQuery(state, 'getStepFactoryVersion').find((args) => args.stepFactoryVersionId === id);
    return ref ? stepFactoriesApi.endpoints.getStepFactoryVersion.select(ref)(state).data : undefined;
};

// the kinds that have a name, and where their page keeps it: RTK Query kinds in the api cache; journeys in the
// `paths` slice, connections in `sources`, connection types in `sourceTypes`; an order opened on its own and one
// opened from a queue both sit in the review item; a queue, a connection or an intent model may only be known from
// its list (the compare page loads the models list, never the model)
const STORE_READERS: Partial<Record<BreadcrumbEntity, StoreReader>> = {
    collection: (state, id) => _whenMatching(state.collections.detail, id, (found) => found.title),
    connection: (state, id) => {
        const slice = state.sources as SourcesSlice;
        return _nameOf(slice.sourceDetail, id) ?? _nameOf(slice.sourceList?.find((source) => source.id === id), id);
    },
    connectionType: (state, id) => _nameOf((state.sourceTypes as DetailSlice).detail, id),
    constant: (state, id) => _nameOf((state.constants as DetailSlice).detail, id),
    environment: (state, id) => _cachedName(selectEnvironment, state, id),
    intent: (state, id) => _nameOf((state.intents as DetailSlice).detail, id),
    intentModel: (state, id) => {
        const slice = state.models as ModelsSlice;
        return _nameOf(slice.detail?.model, id) ?? _nameOf(slice.list?.models?.find((model) => model.id === id), id);
    },
    journey: (state, id) => _nameOf((state.paths as PathsSlice).detail?.path, id),
    order: (state, id) => _whenMatching(state.orderReview.orderReviewItem?.order, id, _poNumber),
    orderReviewItem: (state, id) => _whenMatching(state.orderReview.orderReviewItem, id, (item) => _poNumber(item.order)),
    orderSchema: (state, id) => _cachedName(selectOrderSchema, state, id),
    reviewQueue: (state, id) => {
        const slice = state.reviewQueues as ReviewQueuesSlice;
        return _nameOf(slice.queueDetail, id) ?? _nameOf(slice.queueList?.find((queue) => queue.id === id), id);
    },
    rule: (state, id) => _cachedName(selectRule, state, id),
    ruleSet: (state, id) => _cachedName(selectRuleSet, state, id),
    // the docker pages store these through their thunks, the AI version page through the api cache
    stepFactory: (state, id) => _nameOf((state.stepFactories as StepFactoriesSlice).stepFactoryDetail, id) ?? _cachedName(selectStepFactory, state, id),
    stepFactoryVersion: (state, id) => {
        const version = (state.stepFactories as StepFactoriesSlice).stepFactoryVersionDetail;
        const found = version?.versionId === id ? version : _cachedVersion(state, id);
        return found ? _versionLabel(found) : undefined;
    },
    user: (state, id) => _whenMatching((state.users as UsersSlice).detail, id, _userLabel),
};

/**
 * useCrumbLabel
 * desc: the crumb's text — '' while the name of the entity it stands for is not there yet
 */
const useCrumbLabel = (crumb: Crumb | undefined): string => {
    const entity = crumb?.entity;
    const reader = entity ? STORE_READERS[entity.kind] : undefined;
    const storeName = useAppSelector((state) => (entity && reader ? reader(state, entity.id) : undefined));

    if (!crumb) return '';
    if (crumb.labelKey) return translate(crumb.labelKey);
    if (reader) return storeName ?? '';

    // a kind without a name (trips, rule set evaluations) is its id
    return entity?.id ?? '';
};

export default useCrumbLabel;
